import {
  BrowserCacheLocation,
  PublicClientApplication,
} from '@azure/msal-browser';
import { getIsImpersonation } from './msal/getIsImpersonation';

let token: string;

/**
 * Configurazione per MSAL (Microsoft Authentication Library) utilizzata per l'autenticazione.
 *
 * @constant
 * @type {object}
 * @property {object} auth - Configurazione dell'autenticazione.
 * @property {string} auth.clientId - ID del client, ottenuto dalle variabili d'ambiente.
 * @property {string} auth.authority - URL dell'autorità di autenticazione, costruito utilizzando le variabili d'ambiente.
 * @property {string[]} auth.knownAuthorities - Lista delle autorità conosciute per l'autenticazione.
 * @property {string} auth.redirectUri - URI di reindirizzamento dopo l'autenticazione, ottenuto dalle variabili d'ambiente.
 * @property {object} cache - Configurazione della cache.
 * @property {string} cache.cacheLocation - Posizione della cache, può essere 'localStorage' o 'localStorage'.
 * @property {boolean} cache.storeAuthStateInCookie - Indica se lo stato dell'autenticazione deve essere memorizzato nei cookie.
 */
const getConfig = () => {
  const isImpersonation = getIsImpersonation();

  return {
    auth: {
      clientId: import.meta.env.VITE_NX_PUBLIC_YOUR_CLIENT_ID,
      authority: `https://${import.meta.env.VITE_NX_PUBLIC_YOUR_DOMAIN_NAME}/${
        import.meta.env.VITE_NX_PUBLIC_YOUR_TENANT_NAME
      }.onmicrosoft.com/${
        isImpersonation
          ? import.meta.env.VITE_NX_PUBLIC_POLICY_NAME_IMPERSONATE
          : import.meta.env.VITE_NX_PUBLIC_POLICY_NAME
      }`,
      knownAuthorities: [`${import.meta.env.VITE_NX_PUBLIC_YOUR_DOMAIN_NAME}`],
      redirectUri: isImpersonation
        ? import.meta.env.VITE_NX_PUBLIC_REDIRECT_URI_IMPERSONATE
        : import.meta.env.VITE_NX_PUBLIC_REDIRECT_URI,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      temporaryCacheLocation: BrowserCacheLocation.MemoryStorage,
      storeAuthStateInCookie: true,
    },
  };
};

// Istanza singola di MSAL usata in tutta l'applicazione
export const msalInstance = new PublicClientApplication(getConfig());

// Promise di inizializzazione - viene eseguita una sola volta al caricamento del modulo
const msalInitializationPromise =
  typeof window !== 'undefined' ? msalInstance.initialize() : Promise.resolve();

/**
 * Inizializza MSAL in modo asincrono.
 * Questa funzione può essere chiamata multiple volte in sicurezza,
 * poiché restituisce sempre la stessa promise di inizializzazione.
 */
export const initializeMsal = async () => {
  await msalInitializationPromise;
};

/**
 * Pulisce tutti i cookie che iniziano con "msal".
 * Questa funzione è utile per risolvere problemi di autenticazione
 * rimuovendo i cookie di MSAL vecchi.
 */
export const clearMsalCookies = () => {
  try {
    // Ottieni tutti i cookie
    const cookies = document.cookie.split(';');

    // Filtra e rimuovi i cookie che iniziano con "msal"
    cookies.forEach((cookie) => {
      const cookieName = cookie.split('=')[0].trim();
      if (cookieName.toLowerCase().startsWith('msal')) {
        // Rimuovi il cookie impostandolo con una data di scadenza passata
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname}`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;

        // Prova anche con il dominio principale se siamo in un sottodominio
        const mainDomain = window.location.hostname
          .split('.')
          .slice(-2)
          .join('.');
        if (mainDomain !== window.location.hostname) {
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${mainDomain}`;
        }

        console.log(`Cookie MSAL rimosso: ${cookieName}`);
      }
    });
  } catch (error) {
    console.warn('Errore durante la pulizia dei cookie MSAL:', error);
  }
};

/**
 * Imposta il client Token JWT.
 *
 * @param t - Il token JWT da salvare.
 */
export const setTokenJWT = (t: string) => {
  token = t;
  //@ts-expect-error global variable
  window.token = t;
};

/**
 * Ritorna il token JWT.
 *
 * @return token - il token JWT da utilizzare.
 */
export const getTokenJWT = () => {
  return token;
};
